import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { io, Socket } from 'socket.io-client';
import type { RootState } from '../../store';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  UserCircleIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import EmojiReactions from '../reactions/EmojiReactions';
import { commentsService, type Comment } from '../../services/commentsService';
import ReportModal from '../ui/ReportModal';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3402';

interface CommentsSectionProps {
  postId: string;
  onCommentCountChange?: (count: number) => void;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({ postId, onCommentCountChange }) => {
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ type: 'COMMENT'; id: string; title?: string } | null>(null);

  useEffect(() => {
    loadComments();

    // Setup Socket.io connection for real-time comments
    const token = localStorage.getItem('accessToken');
    if (token) {
      const socket = io(SOCKET_URL, {
        transports: ['websocket'],
        auth: { token }
      });
      
      socketRef.current = socket;
      socket.emit('join_post', postId);

      socket.on('comment_added', (comment: Comment) => {
        setComments(prev => {
          if (comment.parentId) {
            // Add reply to parent comment
            return prev.map(c => 
              c.id === comment.parentId
                ? { ...c, replies: [...(c.replies || []), comment] }
                : c
            );
          } else {
            // Add top-level comment
            return [comment, ...prev];
          }
        });
        if (onCommentCountChange) {
          onCommentCountChange(comments.length + 1);
        }
        scrollToBottom();
      });

      return () => {
        socket.emit('leave_post', postId);
        socket.disconnect();
      };
    } else {
      return undefined;
    }
  }, [postId, onCommentCountChange, comments.length]);

  useEffect(() => {
    scrollToBottom();
  }, [comments]);

  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadComments = async () => {
    try {
      setLoading(true);
      const res = await commentsService.getComments(postId);
      setComments(res.comments || []);
      if (onCommentCountChange) {
        onCommentCountChange(res.comments?.length || 0);
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || submitting || !isAuthenticated) return;

    setSubmitting(true);
    try {
      const res = await commentsService.createComment(postId, { content: newComment.trim() });
      setComments(prev => [res.comment, ...prev]);
      setNewComment('');
      if (onCommentCountChange) {
        onCommentCountChange(comments.length + 1);
      }
      scrollToBottom();
    } catch (error: any) {
      console.error('Failed to post comment:', error);
      alert(error?.response?.data?.message || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (parentId: string, e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim() || submitting || !isAuthenticated) return;

    setSubmitting(true);
    try {
      const res = await commentsService.createComment(postId, { content: replyContent.trim(), parentId });
      setComments(prev => prev.map(c => 
        c.id === parentId
          ? { ...c, replies: [...(c.replies || []), res.comment] }
          : c
      ));
      setReplyContent('');
      setReplyingTo(null);
      scrollToBottom();
    } catch (error: any) {
      console.error('Failed to post reply:', error);
      alert(error?.response?.data?.message || 'Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (commentId: string, e: React.FormEvent) => {
    e.preventDefault();
    if (!editContent.trim() || submitting) return;

    setSubmitting(true);
    try {
      const res = await commentsService.updateComment(postId, commentId, editContent.trim());
      setComments(prev => prev.map(c => 
        c.id === commentId
          ? res.comment
          : {
              ...c,
              replies: c.replies?.map(r => r.id === commentId ? res.comment : r)
            }
      ));
      setEditContent('');
      setEditingComment(null);
    } catch (error: any) {
      console.error('Failed to update comment:', error);
      alert(error?.response?.data?.message || 'Failed to update comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await commentsService.deleteComment(postId, commentId);
      setComments(prev => prev.filter(c => {
        if (c.id === commentId) return false;
        if (c.replies) {
          c.replies = c.replies.filter(r => r.id !== commentId);
        }
        return true;
      }));
      if (onCommentCountChange) {
        onCommentCountChange(Math.max(0, comments.length - 1));
      }
    } catch (error: any) {
      console.error('Failed to delete comment:', error);
      alert(error?.response?.data?.message || 'Failed to delete comment');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  const renderComment = (comment: Comment, isReply = false) => {
    const isOwn = comment.authorId === user?.id;
    const isEditing = editingComment === comment.id;

    return (
      <div key={comment.id} className={`${isReply ? 'ml-8 mt-3' : ''}`}>
        <div className="flex items-start space-x-3">
          {comment.author.avatar ? (
            <img
              src={comment.author.avatar}
              alt={comment.author.username}
              className={`rounded-full object-cover ${isReply ? 'w-8 h-8' : 'w-10 h-10'}`}
            />
          ) : (
            <div className={`rounded-full flex items-center justify-center text-white font-semibold patreon-primary-gradient ${isReply ? 'w-8 h-8 text-sm' : 'w-10 h-10'}`}
              style={{ background: 'var(--patreon-btn-primary-bg)' }}>
              {(comment.author.firstName || comment.author.username).charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className={`bg-gray-50 rounded-lg p-3 ${isReply ? 'text-sm' : ''}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-gray-900">
                    {comment.author.firstName && comment.author.lastName
                      ? `${comment.author.firstName} ${comment.author.lastName}`
                      : comment.author.username}
                  </span>
                  <span className="text-xs text-gray-400">{formatDate(comment.createdAt)}</span>
                </div>
                {isOwn && !isReply && (
                  <div className="flex items-center space-x-2">
                    {!isEditing && (
                      <>
                        <button
                          onClick={() => {
                            setEditingComment(comment.id);
                            setEditContent(comment.content);
                          }}
                          className="p-1 text-gray-400 hover:text-[color:var(--patreon-orange)] transition-colors"
                          title="Edit comment"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(comment.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete comment"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {isEditing && (
                      <button
                        onClick={() => {
                          setEditingComment(null);
                          setEditContent('');
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
                {!isOwn && (
                  <button
                    onClick={() => {
                      setReportTarget({ type: 'COMMENT', id: comment.id, title: comment.content.substring(0, 50) + '...' });
                      setShowReportModal(true);
                    }}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    title="Report comment"
                  >
                    <FlagIcon className="w-4 h-4" />
                  </button>
                )}
              </div>

              {isEditing ? (
                <form onSubmit={(e) => handleEdit(comment.id, e)} className="mt-2">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2 resize-none"
                    style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
                    onFocus={(e) => { e.target.style.borderColor = 'var(--patreon-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                    rows={3}
                    required
                  />
                  <div className="flex items-center justify-end space-x-2 mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingComment(null);
                        setEditContent('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" size="sm" disabled={submitting || !editContent.trim()}>
                      Save
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                  <p className="text-gray-700 whitespace-pre-wrap break-words">{comment.content}</p>
                  {!isReply && (
                    <div className="mt-2">
                      <EmojiReactions
                        targetType="COMMENT"
                        targetId={comment.id}
                        currentUserId={user?.id}
                      />
                    </div>
                  )}
                </>
              )}

              {!isEditing && isAuthenticated && !isReply && (
                <button
                  onClick={() => {
                    setReplyingTo(replyingTo === comment.id ? null : comment.id);
                    setReplyContent('');
                  }}
                  className="mt-2 text-sm" style={{ color: 'var(--patreon-orange)' }}
                >
                  {replyingTo === comment.id ? 'Cancel' : 'Reply'}
                </button>
              )}
            </div>

            {replyingTo === comment.id && (
              <form onSubmit={(e) => handleReply(comment.id, e)} className="mt-2">
                <div className="flex items-start space-x-2">
                  <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Write a reply..."
                    rows={2}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent resize-none"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    disabled={submitting || !replyContent.trim()}
                    leftIcon={<PaperAirplaneIcon className="w-4 h-4" />}
                  >
                    Reply
                  </Button>
                </div>
              </form>
            )}

            {comment.replies && comment.replies.length > 0 && (
              <div className="mt-3 space-y-3">
                {comment.replies.map(reply => renderComment(reply, true))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="mt-6 space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-4 animate-pulse"></div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse"></div>
              <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-6 border-t border-gray-200 pt-6">
      <div className="flex items-center space-x-2 mb-4">
        <ChatBubbleLeftRightIcon className="w-5 h-5 text-gray-600" />
        <h3 className="font-semibold text-gray-900">
          Comments ({comments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0)})
        </h3>
      </div>

      {isAuthenticated && (
        <form onSubmit={handleSubmitComment} className="mb-6">
          <div className="flex items-start space-x-3">
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt={user.username || 'You'}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold patreon-primary-gradient"
                style={{ background: 'var(--patreon-btn-primary-bg)' }}>
                {(user?.firstName || user?.username || 'U').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a comment..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent resize-none mb-2"
              />
              <div className="flex items-center justify-end">
                <Button
                  type="submit"
                  size="sm"
                  disabled={submitting || !newComment.trim()}
                  leftIcon={<PaperAirplaneIcon className="w-4 h-4" />}
                >
                  Post Comment
                </Button>
              </div>
            </div>
          </div>
        </form>
      )}

      {!isAuthenticated && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg text-center">
          <UserCircleIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-2">Please log in to comment</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              window.location.href = '/login';
            }}
          >
            Log In
          </Button>
        </div>
      )}

      <div className="space-y-4">
        {comments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ChatBubbleLeftRightIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p>No comments yet. Be the first to comment!</p>
          </div>
        ) : (
          comments.map(comment => renderComment(comment))
        )}
        <div ref={commentsEndRef} />
      </div>

      {showReportModal && reportTarget && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => {
            setShowReportModal(false);
            setReportTarget(null);
          }}
          targetType={reportTarget.type}
          targetId={reportTarget.id}
          targetTitle={reportTarget.title}
          onReportSubmitted={() => {
            // Could add a toast notification here
          }}
        />
      )}
    </div>
  );
};

export default CommentsSection;


